import moment from "moment";
import { FaRupeeSign } from "react-icons/fa";
import {
  FiCalendar,
  FiArrowRight,
  FiXCircle,
  FiFileText,
  FiDownload,
  FiChevronRight,
  FiLock,
} from "react-icons/fi";
import { IoCalendarOutline } from "react-icons/io5";
import RazorPay from "../../components/RazorPay2.jsx";
import CalendarTabs from "../Forms/CalendarTabs.jsx";
import OldMinimalBookingPage from "./OldMinimalBookingPage.jsx";
import dummyimage from "../../img/default_avatar.jpg";
import yuukke from "../../images/SocialMediaGif/yuukke-white.png";
import woman from "../../img/woman.png";
import { themesPallates, neutralDefaultTheme, hexToRgba } from "../../components/Utils";

// ── Default theme (themeCard values other than 1 or 3). Pulled out of
// CalendarVcard.jsx into its own file since the inline JSX had grown too
// long to navigate comfortably. CalendarVcard now just renders this
// component and passes down state + the handlers it needs.
//
// ── Color system: resolves the SAME theme object ServiceCardThemeV2 and
// ServiceCardThemeV3 use — data.eventDetails.customTheme + themeId looked
// up in themesPallates, falling back to neutralDefaultTheme when no custom
// theme is set. All backgrounds/borders/text below read from this one
// `theme` object instead of ad-hoc hardcoded hex values, so this page can
// never show different "default" colors than V2/V3 for the same profile.
// Flat, light hexToRgba() tints are used instead of gradients per request.
const DefaultThemeProfile = ({
  state,
  setState,
  isMobile,
  isPreview,
  toCamelCase,
  selectService,
  cancelMeeting,
  confirmDetails,
  uploadDetails,
  updateState,
  viewAvailableSlot,
  recaptcha,
  isHovered,
  setIsHovered,
  setShowServiceDropdown,
  showServiceDropdown,
  setScrollPosition,
  setCurrentIndex,
  calYear,
  calMonth,
  setCalYear,
  setCalMonth,
  packageDays,
  timeSlots,
  serviceError,
  setServiceError,
  handleBackFromBooking,
  handleResourceClick,
  updatePaymentStatus,
  API_URL,
  REACT_APP_SITE_KEY,
  optionsMenu,
}) => {
  const reviews = state.reviewList ?? [];
  const reviewRatings = reviews
    .map((r) => Number(r.rating ?? r.stars ?? r.reviewRating))
    .filter((n) => !isNaN(n));
  const avgRating = reviewRatings.length
    ? (reviewRatings.reduce((a, b) => a + b, 0) / reviewRatings.length).toFixed(1)
    : null;

  const resourceList = state.resourceList || [];

  // ── Resolve theme exactly like ServiceCardThemeV2 / V3 ──
  const customThemeItem = state.eventDetails?.customTheme
    ? themesPallates.find((item) => item.id === state.eventDetails.themeId)
    : null;
  const theme = customThemeItem || neutralDefaultTheme;

  return (
    <div
      className="calender-vcard flex-wrap position-relative justify-content-center"
      style={{
        background: hexToRgba(theme.primary, 0.05),
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      {state.paymentPage && (
        <RazorPay
          email={state.email}
          serviceId={state.serviceId}
          name={state.name}
          uId={state.serviceProviderUserId}
          userId={null}
          contactNumber={state.phoneNumber}
          subscriptionId={state.subscribeId}
          amount={state.serviceAmount}
          type={"slot_booking"}
          updatePaymentStatus={updatePaymentStatus}
        />
      )}

      {/* ── Sticky top bar ── */}
      <div
        style={{
          width: "100%",
          flexBasis: "100%",
          flexShrink: 0,
          order: 0,
          position: "sticky",
          top: 0,
          zIndex: 30,
          background: hexToRgba(theme.secondary, 0.85),
          backdropFilter: "blur(8px)",
          borderBottom: `0.5px solid ${theme.border}`,
          padding: isMobile ? "12px 16px" : "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <img
            src={
              state.eventDetails?.businessLogo
                ? `${API_URL}auth/image/${state.eventDetails?.businessLogo}`
                : dummyimage
            }
            alt=""
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = dummyimage;
            }}
            style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: theme.cardForeground,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {toCamelCase(state.eventDetails?.serviceName ?? "Booking")}
          </span>
          <span style={{ fontSize: 12, color: theme.desText, whiteSpace: "nowrap" }}>
            / {state.userDetails?.userName}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!isMobile && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 999,
                border: `1px solid ${theme.border}`,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 500,
                color: theme.desText,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: theme.indiDot }} />
              Available this week
            </span>
          )}
          <button
            type="button"
            onClick={() =>
              document
                .getElementById("service-list-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            style={{
              background: theme.primary,
              color: theme.heading,
              border: "none",
              borderRadius: 999,
              padding: "8px 16px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Book a call
          </button>
        </div>
      </div>

      {state.loader ? (
        <div className="d-flex justify-content-center p-5 align-items-center w-100">
          <div className="spinner-border" role="status" style={{ color: theme.primary }}>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : state.eventDetails && state.userDetails ? (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "20px 14px" : "32px 24px" }}>
          {/* ── HERO ── */}
          <section style={{ marginBottom: isMobile ? 32 : 56 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr",
                gap: 24,
                alignItems: "start",
              }}
            >
              <div>
                {state.userDetails?.profession?.professionName && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: theme.desText,
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: theme.primary }} />
                    {state.userDetails.profession.professionName}
                  </span>
                )}
                <h1
                  style={{
                    fontSize: "clamp(32px, 6vw, 56px)",
                    fontWeight: 700,
                    color: theme.cardForeground,
                    margin: "0 0 14px",
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                    textTransform: "capitalize",
                  }}
                >
                  {state.userDetails.userName}
                </h1>
                {(state.eventDetails?.description || state.serviceDetails?.description) && (
                  <div
                    style={{ fontSize: 14, color: theme.desText, lineHeight: 1.6, maxWidth: 520, margin: "0 0 16px" }}
                    dangerouslySetInnerHTML={{
                      __html: state.eventDetails?.description || state.serviceDetails?.description,
                    }}
                  />
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  {state.eventDetails?.timeZone && (
                    <span
                      style={{
                        borderRadius: 999,
                        border: `1px solid ${theme.border}`,
                        padding: "6px 12px",
                        fontSize: 12,
                        color: theme.desText,
                      }}
                    >
                      {state.eventDetails.timeZone}
                    </span>
                  )}
                  {state.language?.length > 0 && (
                    <span
                      style={{
                        borderRadius: 999,
                        border: `1px solid ${theme.border}`,
                        padding: "6px 12px",
                        fontSize: 12,
                        color: theme.desText,
                      }}
                    >
                      {state.language.map((l) => l.language).join(" · ")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {state.socialMedia.map((item, i) => (
                    <a
                      key={i}
                      href={item.link}
                      onClick={(e) => {
                        if (isPreview) e.preventDefault();
                      }}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        border: `1px solid ${theme.border}`,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <img src={item.icon} alt="" style={{ width: 16, height: 16, objectFit: "contain" }} />
                    </a>
                  ))}
                  {avgRating && (
                    <span style={{ fontSize: 13, color: theme.cardForeground, fontWeight: 600 }}>
                      ★ {avgRating} · {reviews.length} review{reviews.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats card */}
              <div
                style={{
                  background: theme.card,
                  borderRadius: 24,
                  padding: 24,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  border: `1px solid ${theme.border}`,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: `3px solid ${theme.thinBg}`,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={
                      state.eventDetails?.showProfileImage
                        ? `${API_URL}api/getUserPhoto/${state.userDetails.userId}/name`
                        : yuukke
                    }
                    alt="user"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = dummyimage;
                    }}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: theme.cardForeground, textTransform: "capitalize" }}>
                    {state.userDetails.userName}
                  </div>
                  {state.userDetails?.profession?.professionName && (
                    <div style={{ fontSize: 12, color: theme.desText }}>
                      {state.userDetails.profession.professionName}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", width: "100%", borderTop: `1px solid ${theme.border}`, paddingTop: 12, gap: 8 }}>
                  {state.bookingCount != null && (
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: theme.cardForeground }}>{state.bookingCount}</div>
                      <div style={{ fontSize: 10, color: theme.desText, textTransform: "uppercase" }}>Bookings</div>
                    </div>
                  )}
                  {avgRating && (
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: theme.cardForeground }}>{avgRating}</div>
                      <div style={{ fontSize: 10, color: theme.desText, textTransform: "uppercase" }}>Rating</div>
                    </div>
                  )}
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: theme.cardForeground }}>{reviews.length}</div>
                    <div style={{ fontSize: 10, color: theme.desText, textTransform: "uppercase" }}>Reviews</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1.15fr",
              gap: 24,
              alignItems: "start",
            }}
          >
            {/* ══ LEFT ══ */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {state.eventDetails && (
                <CalendarTabs
                  eventDetails={state.eventDetails}
                  customTheme={false}
                  contact={state.userDetails}
                  clarityService={state.clarityService}
                  isPreview={isPreview}
                  viewClarityService={(data) => selectService(data, "clarity")}
                />
              )}

              {/* ── Services ── */}
              <section
                id="service-list-section"
                style={{
                  background: theme.card,
                  borderRadius: 24,
                  padding: isMobile ? 20 : 28,
                  border: `1px solid ${theme.border}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    marginBottom: 18,
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: theme.desText,
                        marginBottom: 4,
                      }}
                    >
                      01 — Services
                    </div>
                    <h4 className="m-0" style={{ fontWeight: 700, fontSize: "clamp(18px, 3vw, 22px)", color: theme.cardForeground }}>
                      Pick what you need
                    </h4>
                  </div>
                  <span style={{ fontSize: 12, color: theme.desText }}>{state.serviceList.length} available</span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: 12,
                  }}
                >
                  {state.serviceList.map((item, i) => {
                    const active = item.id === state.serviceId;
                    return (
                      <div
                        key={i}
                        onClick={() => {
                          if (isPreview) return;
                          selectService(item);
                          if (isMobile) {
                            setTimeout(() => {
                              document
                                .getElementById("booking-panel-section")
                                ?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 80);
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.transform = "translateY(0)";
                        }}
                        className="cal-small-box box-rd text-capitalize d-flex flex-column"
                        style={{
                          position: "relative",
                          cursor: "pointer",
                          padding: 18,
                          borderRadius: 18,
                          background: active ? theme.primary : theme.card,
                          color: active ? theme.heading : theme.cardForeground,
                          border: active ? "1.5px solid transparent" : `1px solid ${theme.border}`,
                          boxShadow: active
                            ? `0 8px 24px ${hexToRgba(theme.primary, 0.28)}`
                            : "0 1px 3px rgba(0,0,0,0.05)",
                          transition: "transform 0.15s, box-shadow 0.15s",
                        }}
                      >
                        {!active && (
                          <span
                            style={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: theme.indiDot,
                            }}
                          />
                        )}
                        <h4
                          className="m-0 three-line cal-heading"
                          style={{ fontWeight: "600", textTransform: "capitalize", color: active ? theme.heading : theme.cardForeground }}
                        >
                          {toCamelCase(item.serviceName ?? "")}
                        </h4>
                        <h6
                          className="cal-sub-heading mt-2"
                          style={{ color: active ? theme.subHeading : theme.desText }}
                        >
                          {item.appointmentValue}
                        </h6>
                        <section
                          className="gray-box mt-auto"
                          style={{ background: active ? "rgba(255,255,255,0.15)" : theme.thinBg }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <IoCalendarOutline style={{ color: active ? theme.heading : theme.tagtxtCol }} className="svg-icons" />
                            <p className="d-flex flex-column ml-2">
                              <span
                                className="cal-sub-heading font-weight-bold"
                                style={{ color: active ? theme.heading : theme.cardForeground }}
                              >
                                {item.duration} mins
                              </span>
                              <span
                                className="cal-sub-heading"
                                style={{ color: active ? theme.subHeading : theme.desText }}
                              >
                                Video Meeting
                              </span>
                            </p>
                          </div>
                          <span className="gray-box font-weight-bold p-2" style={{ color: active ? theme.heading : theme.cardForeground }}>
                            <FaRupeeSign className="mr-1" />
                            {item.amount}
                          </span>
                        </section>
                      </div>
                    );
                  })}
                  {state.serviceList.length === 0 && <div className="text-muted">No services found</div>}
                </div>
              </section>

              {/* ── Resources — direct-buy items, no calendar/slot flow. Same
                  card language as Services above, "Resource" tag + file
                  count instead of duration/meeting type. Only rendered
                  when resources actually exist. ── */}
              {resourceList.length > 0 && (
                <section
                  style={{
                    background: theme.card,
                    borderRadius: 24,
                    padding: isMobile ? 20 : 28,
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      marginBottom: 18,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.14em",
                          color: theme.desText,
                          marginBottom: 4,
                        }}
                      >
                        01B — Resources
                      </div>
                      <h4 className="m-0" style={{ fontWeight: 700, fontSize: "clamp(18px, 3vw, 22px)", color: theme.cardForeground }}>
                        Get instant access
                      </h4>
                    </div>
                    <span style={{ fontSize: 12, color: theme.desText }}>{resourceList.length} available</span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {resourceList.map((res) => {
                      const isResourceFree = Number(res.amount) === 0;
                      const fileCount = res.resourceFileNames?.length || 0;
                      return (
                        <div
                          key={res.id}
                          onClick={() => {
                            if (isPreview) return;
                            handleResourceClick(res);
                          }}
                          className="cal-small-box box-rd text-capitalize d-flex flex-column"
                          style={{
                            position: "relative",
                            cursor: "pointer",
                            padding: 18,
                            borderRadius: 18,
                            background: theme.card,
                            color: theme.cardForeground,
                            border: `1px solid ${theme.border}`,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            transition: "transform 0.15s, box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                        >
                          <span
                            style={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              color: theme.desText,
                            }}
                          >
                            Resource
                          </span>
                          <h4 className="m-0 three-line cal-heading" style={{ fontWeight: "600", textTransform: "capitalize" }}>
                            {toCamelCase(res.serviceName ?? "")}
                          </h4>
                          <h6 className="cal-sub-heading mt-2" style={{ color: theme.desText }}>
                            {res.description}
                          </h6>
                          <section className="gray-box mt-auto" style={{ background: theme.thinBg }}>
                            <div className="d-flex align-items-center justify-content-center">
                              <FiFileText style={{ color: theme.tagtxtCol }} className="svg-icons" />
                              <p className="d-flex flex-column ml-2">
                                <span className="cal-sub-heading font-weight-bold" style={{ color: theme.cardForeground }}>
                                  {fileCount} file{fileCount !== 1 ? "s" : ""}
                                </span>
                                <span className="cal-sub-heading" style={{ color: theme.desText }}>
                                  {isResourceFree ? "Free download" : "Instant access"}
                                </span>
                              </p>
                            </div>
                            <span className="gray-box font-weight-bold p-2" style={{ color: theme.primary }}>
                              {isResourceFree ? (
                                <FiDownload className="mr-1" />
                              ) : (
                                <>
                                  <FaRupeeSign className="mr-1" />
                                  {res.amount}
                                </>
                              )}
                            </span>
                          </section>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* How it works */}
              <section
                style={{
                  background: theme.card,
                  borderRadius: 24,
                  padding: isMobile ? 20 : 28,
                  border: `1px solid ${theme.border}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.14em",
                    color: theme.desText,
                    marginBottom: 4,
                  }}
                >
                  02 — How it works
                </div>
                <h4
                  className="m-0"
                  style={{ fontWeight: 700, fontSize: "clamp(18px, 3vw, 22px)", color: theme.cardForeground, marginBottom: 18 }}
                >
                  Four steps, zero back-and-forth
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
                    gap: 12,
                  }}
                >
                  {[
                    { n: "01", title: "Pick a service", desc: "Choose what fits what you need." },
                    { n: "02", title: "Grab a slot", desc: "Live availability, updated in real time." },
                    { n: "03", title: "Confirm details", desc: "Share a few lines about what to cover." },
                    { n: "04", title: "You're booked", desc: "Instant confirmation, no waiting." },
                  ].map((s) => (
                    <div key={s.n} style={{ borderRadius: 16, background: theme.thinBg, padding: 16 }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: theme.border }}>{s.n}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.cardForeground, marginTop: 6 }}>{s.title}</div>
                      <div style={{ fontSize: 11, color: theme.desText, marginTop: 4, lineHeight: 1.4 }}>{s.desc}</div>
                    </div>
                  ))}
                </div>
              </section>

              {state.userUpcomingMeting?.length > 0 && (
                <div
                  className="card-v3"
                  style={{ background: theme.card, borderRadius: "12px", boxShadow: "#000940 1px 1px 1px", overflow: "hidden" }}
                >
                  <div className="p-4">
                    <h3 style={{ color: theme.cardForeground, fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem" }}>
                      Upcoming Meetings
                    </h3>
                    <div className="c-gallery p-0" style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <ul className="d-flex flex-column m-0 p-0" style={{ gap: "1.5rem" }}>
                        {state.userUpcomingMeting.map((item, i) => (
                          <li
                            key={i}
                            className="p-4"
                            style={{ background: theme.thinBg, borderRadius: "8px", border: `1px solid ${theme.border}` }}
                          >
                            <div style={{ textAlign: "start" }}>
                              <div className="d-flex justify-content-between align-items-start">
                                <b style={{ color: theme.cardForeground, fontSize: "1.3rem", lineHeight: "1.5" }}>
                                  {item.serviceName}
                                </b>
                                <button
                                  onClick={() => cancelMeeting(item)}
                                  style={{
                                    background: theme.primary,
                                    color: theme.heading,
                                    padding: "0.5rem 1rem",
                                    borderRadius: "6px",
                                    border: "none",
                                    fontWeight: "500",
                                    fontSize: "0.6rem",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <FiXCircle size={16} /> Cancel
                                </button>
                              </div>
                              <p
                                className="d-flex align-items-center mt-2"
                                style={{ color: theme.desText, fontSize: "0.615rem", gap: "0.5rem" }}
                              >
                                <FiCalendar size={14} />
                                <span>{moment(item.startTime).locale("en").format("lll")}</span>
                                <FiArrowRight size={14} />
                                <span>{moment(item.endTime).locale("en").format("lll")}</span>
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {state.recommendList?.length > 0 && (
                <div className="card-v3" style={{ background: theme.card, borderRadius: "12px", boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)" }}>
                  <div className="p-4">
                    <h3 style={{ color: theme.cardForeground, fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem" }}>
                      Recommendations
                    </h3>
                    <ul className="d-flex flex-column m-0 p-0" style={{ gap: "1.5rem", maxHeight: "400px", overflowY: "auto" }}>
                      {state.recommendList.map((item, i) => (
                        <li
                          key={i}
                          className="p-4"
                          style={{ background: theme.thinBg, borderRadius: "8px", border: `1px solid ${theme.border}` }}
                        >
                          <p
                            className="f-12"
                            style={{
                              color: theme.desText,
                              fontSize: "0.875rem",
                              lineHeight: "1.6",
                              fontStyle: "italic",
                              marginBottom: "1rem",
                            }}
                          >
                            "{item.suggestion}"
                          </p>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden", flexShrink: "0" }}>
                              <img
                                src={item.mediaFile ? `${API_URL}auth/image/${item.mediaFile}` : woman}
                                alt={item.name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = woman;
                                }}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            </div>
                            <h6 style={{ color: theme.cardForeground, fontWeight: "600" }}>{item.name}</h6>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {reviews.length > 0 && (
                <section
                  style={{
                    background: theme.card,
                    borderRadius: 24,
                    padding: isMobile ? 20 : 28,
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      marginBottom: 18,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.14em",
                          color: theme.desText,
                          marginBottom: 4,
                        }}
                      >
                        03 — Reviews
                      </div>
                      <h4 className="m-0" style={{ fontWeight: 700, fontSize: "clamp(18px, 3vw, 22px)", color: theme.cardForeground }}>
                        What people say
                      </h4>
                    </div>
                    {avgRating && <span style={{ fontSize: 12, color: theme.desText }}>★ {avgRating} · {reviews.length}</span>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, cursor: "pointer" }}>
                    {reviews.slice(0, 3).map((r, i) => {
                      const reviewerName = r.name ?? r.userName ?? r.reviewerName ?? "Anonymous";
                      const reviewText = r.comment ?? r.review ?? r.text ?? r.suggestion ?? "";
                      const rating = Number(r.rating ?? r.stars ?? r.reviewRating ?? 0);
                      const role = r.role ?? r.designation ?? "";
                      const when = r.date ?? r.createdAt ?? null;
                      return (
                        <div key={i} style={{ background: theme.thinBg, borderRadius: 16, padding: 18 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  background: theme.firstTagBg,
                                  display: "grid",
                                  placeItems: "center",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: theme.cardForeground,
                                }}
                              >
                                {reviewerName[0]}
                              </div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: theme.cardForeground }}>{reviewerName}</div>
                                {role && <div style={{ fontSize: 11, color: theme.desText }}>{role}</div>}
                              </div>
                            </div>
                            {rating > 0 && <div style={{ fontSize: 12, color: theme.cardForeground }}>{"★".repeat(Math.round(rating))}</div>}
                          </div>
                          {reviewText && (
                            <p style={{ fontSize: 13, color: theme.desText, lineHeight: 1.5, margin: "10px 0 0" }}>"{reviewText}"</p>
                          )}
                          {when && (
                            <div style={{ fontSize: 11, color: theme.desText, marginTop: 6 }}>
                              {moment(when).isValid() ? moment(when).locale("en").fromNow() : when}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>

            {/* ══ RIGHT: sticky booking panel ══ */}
            <div id="booking-panel-section" style={{ position: isMobile ? "static" : "sticky", top: 80, scrollMarginTop: 70 }}>
              {!state.bookingPage ? (
                <section
                  style={{
                    background: theme.primary,
                    borderRadius: 24,
                    padding: isMobile ? 24 : 32,
                    color: theme.heading,
                    boxShadow: `0 12px 32px ${hexToRgba(theme.primary, 0.3)}`,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.7, marginBottom: 8 }}>
                    Ready to book
                  </div>
                  <h3 style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, margin: "0 0 10px" }}>
                    Pick a service to
                    <br />
                    begin booking.
                  </h3>
                  <p style={{ fontSize: 13, opacity: 0.8, margin: "0 0 24px" }}>
                    Choose a service above — the calendar and time slots will open here.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
                    {["Pick a service", "Choose date & time", "Enter your details"].map((label, i) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            border: "1px solid rgba(255,255,255,0.4)",
                            display: "grid",
                            placeItems: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {i + 1}
                        </span>
                        <span style={{ fontSize: 13 }}>{label}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById("service-list-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                    style={{
                      width: "100%",
                      background: theme.heading,
                      color: theme.primary,
                      border: "none",
                      borderRadius: 999,
                      padding: "12px 20px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Browse services
                  </button>
                </section>
              ) : (
                <OldMinimalBookingPage
                  state={state}
                  setState={setState}
                  updateState={updateState}
                  isHovered={isHovered}
                  setIsHovered={setIsHovered}
                  setShowServiceDropdown={setShowServiceDropdown}
                  setScrollPosition={setScrollPosition}
                  setCurrentIndex={setCurrentIndex}
                  API_URL={API_URL}
                  calYear={calYear}
                  calMonth={calMonth}
                  setCalMonth={setCalMonth}
                  setCalYear={setCalYear}
                  packageDays={packageDays}
                  viewAvailableSlot={viewAvailableSlot}
                  timeSlots={timeSlots}
                  confirmDetails={confirmDetails}
                  showServiceDropdown={showServiceDropdown}
                  serviceError={serviceError}
                  setServiceError={setServiceError}
                  uploadDetails={uploadDetails}
                  recaptcha={recaptcha}
                  isMobile={isMobile}
                  REACT_APP_SITE_KEY={REACT_APP_SITE_KEY}
                  optionsMenu={optionsMenu}
                  onBack={handleBackFromBooking}
                  onResourceClick={handleResourceClick}
                  hideProfile
                />
              )}
            </div>
          </div>

          <footer style={{ textAlign: "center", padding: "32px 0 12px", fontSize: 12, color: theme.desText }}>
            Powered by{" "}
            <a href="https://yuukke.com/" style={{ color: theme.desText, textDecoration: "underline" }}>
              Yuukke
            </a>
          </footer>
        </div>
      ) : (
        <div className="text-center w-100 font-bold" style={{ color: theme.cardForeground, padding: "3rem 1rem" }}>
          Something went wrong, please visit <span style={{ color: theme.desText }}>Yuukke</span>
        </div>
      )}
    </div>
  );
};

export default DefaultThemeProfile;